import { useEffect, useState } from 'react';
import memoryMonitor, {
  MemoryInfo,
  MemoryMonitorOptions,
} from '../memoryMonitor';

/**
 * Custom hook for memory monitoring
 * @param options Memory monitoring options
 * @returns Memory monitoring data and controls
 */
export const useMemoryMonitor = (
  options: Partial<MemoryMonitorOptions> = {},
) => {
  const [memoryInfo, setMemoryInfo] = useState<MemoryInfo | null>(
    memoryMonitor.getLatestSample(),
  );
  const [isActive, setIsActive] = useState<boolean>(memoryMonitor.isActive());
  const [stats, setStats] = useState<any>(memoryMonitor.getStats());

  // Configure memory monitor
  useEffect(() => {
    // Only run in development environment by default
    const isDevEnv = process.env.NODE_ENV === 'development';
    const shouldRun =
      options.enabled !== undefined ? options.enabled : isDevEnv;

    if (!shouldRun || !memoryMonitor.isBrowserSupported()) {
      return;
    }

    // Configure the monitor with provided options
    const monitorOptions: MemoryMonitorOptions = {
      ...options,
      onSample: (info: MemoryInfo) => {
        setMemoryInfo(info);
        setStats(memoryMonitor.getStats());
        options.onSample?.(info);
      },
    };

    // Start monitoring if not already active
    if (!memoryMonitor.isActive()) {
      memoryMonitor.start();
      setIsActive(true);
    }

    // Set up interval for UI updates
    const updateInterval = setInterval(() => {
      const sample = memoryMonitor.takeSample();
      if (sample) {
        setMemoryInfo(sample);
        setStats(memoryMonitor.getStats());
      }
    }, options.interval || 5000);

    return () => {
      clearInterval(updateInterval);
      // Don't stop the monitor on unmount as it might be used elsewhere
    };
  }, [options.enabled, options.interval]);

  // Functions to control the monitor
  const startMonitoring = () => {
    memoryMonitor.start();
    setIsActive(true);
  };

  const stopMonitoring = () => {
    memoryMonitor.stop();
    setIsActive(false);
  };

  const takeSample = () => {
    const sample = memoryMonitor.takeSample();
    if (sample) {
      setMemoryInfo(sample);
      setStats(memoryMonitor.getStats());
    }
    return sample;
  };

  const clearSamples = () => {
    memoryMonitor.clearSamples();
    setStats(memoryMonitor.getStats());
  };

  const exportData = () => {
    return memoryMonitor.exportData();
  };

  // Function to get all samples
  const getSamples = () => {
    return memoryMonitor.getSamples();
  };

  return {
    memoryInfo,
    stats,
    isActive,
    isSupported: memoryMonitor.isBrowserSupported(),
    startMonitoring,
    stopMonitoring,
    takeSample,
    clearSamples,
    exportData,
    getSamples,
  };
};

export default useMemoryMonitor;
